import { useTranslation } from 'react-i18next';
import { OrderStatusTab } from '@/types/columns';
import { OrderFilters, useOrderFilters } from '@/stores/useFilterOrderStore';
import { getOrderParams } from '@/utils/params';

/**
 * BOXY-376 — the orders list on screen, as an export query.
 *
 * `filter_list` names the list the way the backend's list endpoints do, so the
 * server resolves the very statuses the list shows (a sub-tab without its own
 * endpoint is its status slug, e.g. `resend`). The list's own filters (dates,
 * type, carrier, printed) ride along; with no date filter the export has no
 * date window either — the list on screen has none.
 */
const LIST_BY_TAB: Record<OrderStatusTab, string> = {
  [OrderStatusTab.READY_TO_SHIP]: 'ready-to-ship',
  [OrderStatusTab.IN_PROGRESS]: 'in-progress',
  [OrderStatusTab.ON_HOLD]: 'on-hold',
  [OrderStatusTab.RTO]: 'rto',
  [OrderStatusTab.FINISHED]: 'finished',
  [OrderStatusTab.INVENTORY]: 'inventory',
};

// Sub-tabs the dashboard fetches from a dedicated list endpoint (useOrderData).
const LIST_BY_SUB_TAB: Record<string, string> = {
  'action-required': 'action-required',
  'en-route': 'en-route',
  collecting: 'collecting',
  'rto-requested': 'rto-request',
  'rto-in-transit': 'returning-origin',
  'rto-received-warehouse': 'returned-warehouse',
};

const TAB_LABEL: Record<OrderStatusTab, string> = {
  [OrderStatusTab.READY_TO_SHIP]: 'with_you',
  [OrderStatusTab.IN_PROGRESS]: 'in_progress',
  [OrderStatusTab.ON_HOLD]: 'problematic',
  [OrderStatusTab.RTO]: 'return_to_origin',
  [OrderStatusTab.FINISHED]: 'completed',
  [OrderStatusTab.INVENTORY]: 'merchant_title_order_status.inventory',
};

export interface CurrentListExport {
  /** i18n keys for "<tab> › <sub-tab>". */
  labelKeys: string[];
  /** The `filter_list` value — also the file-name slug of a "This tab" export. */
  list: string;
  query: string;
}

export const buildCurrentListExport = (
  tab: OrderStatusTab,
  subTab: string,
  filters: OrderFilters,
): CurrentListExport => {
  const hasSubTab = tab !== OrderStatusTab.INVENTORY && subTab && subTab !== 'all';
  const list = hasSubTab ? (LIST_BY_SUB_TAB[subTab] ?? subTab) : LIST_BY_TAB[tab];

  const params = new URLSearchParams({ filter_list: list });
  getOrderParams(filters)?.forEach((value, key) => params.append(key, value));

  const subLabel =
    subTab === 'action-required' ? 'action_required' : `merchant_title_order_status.${subTab}`;

  return {
    labelKeys: hasSubTab ? [TAB_LABEL[tab], subLabel] : [TAB_LABEL[tab]],
    list,
    query: params.toString(),
  };
};

const useCurrentListExport = () => {
  const { t } = useTranslation();
  // Read off the URL the orders page keeps in sync (activeTab / activeSubTab).
  // Not useSearchParams: the header also renders outside a Router (its tests),
  // and opening the export modal re-renders, so this is read when it matters.
  const searchParams = new URLSearchParams(window.location.search);
  const orderFilters = useOrderFilters();

  const tabParam = searchParams.get('activeTab') as OrderStatusTab | null;
  const tab = tabParam && tabParam in TAB_LABEL ? tabParam : OrderStatusTab.READY_TO_SHIP;
  const current = buildCurrentListExport(
    tab,
    searchParams.get('activeSubTab') ?? 'all',
    orderFilters,
  );

  return {
    label: current.labelKeys.map((key) => String(t(key))).join(' › '),
    slug: current.list,
    query: current.query,
  };
};

export default useCurrentListExport;
